import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, FileText, ShoppingCart, Users, Settings, Languages, Sun, Moon, Wallet, Landmark, BookOpen, UserCog, LogOut, KeyRound, Palette } from "lucide-react";
import { Logo, LogoMark } from "@/components/Logo";
import { useLang } from "@/i18n";
import { useAuth, canModule, isAdminRole } from "@/auth";
import { cn } from "@/lib/utils";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";

const NAV = [
  { to: "/", key: "nav_dashboard", module: "dashboard", icon: LayoutDashboard, id: "sidebar-nav-dashboard" },
  { to: "/documents?group=sales", key: "nav_sales", module: "documents", icon: FileText, id: "sidebar-nav-sales", match: (p, s) => p.startsWith("/documents") && s.includes("group=sales") },
  { to: "/documents?group=purchases", key: "nav_purchases", module: "documents", icon: ShoppingCart, id: "sidebar-nav-purchases", match: (p, s) => p.startsWith("/documents") && s.includes("group=purchases") },
  { to: "/contacts", key: "nav_contacts", module: "contacts", icon: Users, id: "sidebar-nav-contacts" },
  { to: "/payroll", key: "nav_payroll", module: "payroll", icon: Wallet, id: "sidebar-nav-payroll" },
  { to: "/mydata", key: "nav_mydata", module: "mydata", icon: Landmark, id: "sidebar-nav-mydata" },
  { to: "/ledger", key: "nav_ledger", module: "ledger", icon: BookOpen, id: "sidebar-nav-ledger" },
  { to: "/settings", key: "nav_settings", module: "settings", icon: Settings, id: "sidebar-nav-settings" },
  { to: "/users", key: "nav_users", admin: true, icon: UserCog, id: "sidebar-nav-users" },
  { to: "/brand", key: "nav_brand", admin: true, icon: Palette, id: "sidebar-nav-brand" },
];

function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, () => setDark((d) => !d)];
}

export const Layout = () => {
  const { t, lang, toggle } = useLang();
  const { user, logout } = useAuth();
  const [dark, toggleTheme] = useTheme();
  const [pwdOpen, setPwdOpen] = useState(false);
  const { pathname, search } = useLocation();
  const nav = NAV.filter((n) => (n.admin ? isAdminRole(user) : canModule(user, n.module)));
  const isOn = (n) => (n.match ? n.match(pathname, search) : n.to === "/" ? pathname === "/" : pathname.startsWith(n.to));
  const current = nav.find(isOn) || (pathname.startsWith("/documents") ? { key: "nav_documents" } : null);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-body">
      <aside className="w-64 flex-shrink-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hidden md:flex flex-col justify-between sticky top-0 h-screen z-30 no-print">
        <div>
          <div className="h-16 px-5 flex items-center border-b border-slate-200 dark:border-slate-800">
            <Logo size={34} subtitle={t("app_subtitle")} data-testid="app-title" />
          </div>
          <nav className="p-3 space-y-1 mt-2">
            {nav.map((n) => { const { to, key, icon: Icon, id } = n; return (
              <NavLink key={to} to={to} end={to === "/"} data-testid={id}
                className={() => cn("nav-link", isOn(n) && "active")}>
                <Icon size={18} strokeWidth={2} />
                <span>{t(key)}</span>
              </NavLink>
            ); })}
          </nav>
        </div>
        <div className="p-4 border-t border-slate-200 dark:border-slate-800 space-y-2" data-testid="sidebar-user-box">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-300 flex items-center justify-center font-heading font-bold text-sm uppercase">{user?.username?.[0]}</div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="sidebar-username">{user?.full_name || user?.username}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{t(`role_${user?.role}`)}</div>
            </div>
          </div>
          <div className="flex gap-1">
            <button onClick={() => setPwdOpen(true)} data-testid="change-password-button" className="flex-1 h-8 inline-flex items-center justify-center gap-1 rounded border border-slate-200 dark:border-slate-700 text-xs hover:bg-slate-100 dark:hover:bg-slate-800"><KeyRound size={13} />{t("password")}</button>
            <button onClick={logout} data-testid="logout-button" className="flex-1 h-8 inline-flex items-center justify-center gap-1 rounded border border-slate-200 dark:border-slate-700 text-xs hover:bg-rose-50 dark:hover:bg-rose-950 text-rose-600"><LogOut size={13} />{t("logout")}</button>
          </div>
        </div>
      </aside>
      <ChangePasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 border-b border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md px-4 sm:px-6 flex items-center justify-between sticky top-0 z-20 no-print">
          <div className="flex items-center gap-3 min-w-0">
            <LogoMark size={30} className="md:hidden text-blue-800" />
            <h2 className="font-heading font-bold text-base md:text-lg truncate" data-testid="page-title">
              {current ? t(current.key) : t("nav_documents")}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggle} data-testid="language-toggle-button"
              className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              <Languages size={16} />
              <span className={cn(lang === "el" ? "text-blue-700 dark:text-blue-300" : "text-slate-400")}>ΕΛ</span>
              <span className="text-slate-300">|</span>
              <span className={cn(lang === "en" ? "text-blue-700 dark:text-blue-300" : "text-slate-400")}>EN</span>
            </button>
            <button onClick={toggleTheme} data-testid="theme-toggle-button" aria-label={t("theme")}
              className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              {dark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button onClick={logout} data-testid="logout-button-mobile" aria-label={t("logout")}
              className="md:hidden h-9 w-9 inline-flex items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950">
              <LogOut size={16} />
            </button>
          </div>
        </header>

        <nav className="md:hidden flex overflow-x-auto border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 no-print">
          {nav.map((n) => { const { to, key, icon: Icon } = n; return (
            <NavLink key={to} to={to} end={to === "/"}
              className={() => cn("flex-1 shrink-0 min-w-[64px] flex flex-col items-center gap-1 py-2 text-[10px] font-medium",
                isOn(n) ? "text-blue-700 dark:text-blue-300 border-b-2 border-blue-700" : "text-slate-500")}>
              <Icon size={16} />
              <span className="truncate max-w-full px-1">{t(key)}</span>
            </NavLink>
          ); })}
        </nav>

        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 space-y-6 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
